import React from "react";
import { useAlertsOverview, useLeaderboardAlerts } from "@/hooks/useCryptoAlerts";
import { TokenIcon } from "@/components/TokenIcon";
import { exchangeBrand } from "@/lib/exchange-brand";
import { formatTimeAgo } from "@/hooks/useAlertCardData";

/** Donut colours in Figma order; the sixth slice is always "Other". */
const COLORS = ["#eab308", "#3b82f6", "#a855f7", "#ef4444", "#22c55e", "#737373"];
const MAX_SLICES = 5;
const RADIUS = 15.9155; // circumference 100, so dash lengths are percentages

interface AlertsInsightsSidebarProps {
  onAnalyze: () => void;
  onViewAll?: () => void;
}

/**
 * Desktop right column of the Alerts page (Figma "Right"): AI insight,
 * where the last 7 days of listings came from, and the latest listings.
 */
export const AlertsInsightsSidebar: React.FC<AlertsInsightsSidebarProps> = ({
  onAnalyze,
  onViewAll,
}) => {
  const { data: overview } = useAlertsOverview();
  const { data: leaderboard } = useLeaderboardAlerts();
  const recent = (leaderboard?.listings ?? []).slice(0, 5);

  const total = overview?.listings.count ?? 0;
  const rows = overview?.byExchange ?? [];
  const top = rows.slice(0, MAX_SLICES);
  const other = rows.slice(MAX_SLICES).reduce((sum, r) => sum + r.count, 0);
  const slices = [
    ...top.map((r) => ({ key: r.exchange, label: exchangeBrand(r.exchange).name, count: r.count })),
    ...(other > 0 ? [{ key: "other", label: "Other", count: other }] : []),
  ];
  let offset = 0;
  const segments = slices.map((slice, i) => {
    const share = total ? (slice.count / total) * 100 : 0;
    const segment = {
      ...slice,
      share,
      offset,
      color: slice.key === "other" ? COLORS[5] : COLORS[i],
    };
    offset += share;
    return segment;
  });

  return (
    <div className="flex w-[320px] shrink-0 flex-col gap-6">
      {/* AI insight */}
      <div className="relative flex flex-col gap-4 overflow-hidden rounded-xl border border-[rgba(45,204,137,0.24)] bg-primary px-3 py-4">
        <img
          src="/ai-insight.jpg"
          alt=""
          className="pointer-events-none absolute inset-0 size-full object-cover opacity-20"
        />
        <div className="relative flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <img src="/icons/brain-ai.svg" alt="" width={24} height={24} />
            <p className="text-base font-semibold leading-6 text-white">AI Market Insight</p>
          </div>
          <p className="w-[220px] text-sm leading-5 text-[#d4d4d4]">
            {overview
              ? `${overview.listings.count} new listings across major exchanges this week, ${overview.hot.count} ran ${overview.hot.thresholdPercent}%+ after their alert. Get an AI read before you trade.`
              : "Get an AI read on any token before you trade it."}
          </p>
        </div>
        <button
          type="button"
          onClick={onAnalyze}
          className="relative flex items-center gap-1 self-start rounded-xl border-2 border-white/10 bg-[#07a66c] px-3.5 py-2.5 text-sm font-semibold leading-5 text-white hover:bg-[#08b877]"
        >
          Analyze New Listings
          <img src="/icons/figma/arrow-right.svg" alt="" width={20} height={20} />
        </button>
      </div>

      {/* Exchange distribution */}
      <div className="glass flex flex-col gap-4 px-3 py-4 backdrop-blur-[6px]">
        <div className="flex items-baseline justify-between">
          <p className="text-base font-semibold leading-6 text-white">Exchange Distribution</p>
          <span className="text-xs text-[#a3a3a3]">Last 7 days</span>
        </div>
        {!overview ? (
          <div className="h-40 animate-pulse rounded-lg bg-white/5" />
        ) : total === 0 ? (
          <p className="text-sm text-[#a3a3a3]">No listings in the last 7 days.</p>
        ) : (
          <div className="flex items-center gap-4">
            <div className="relative size-[160px] shrink-0">
              <svg viewBox="0 0 42 42" className="size-full -rotate-90">
                {segments.map((s) => (
                  <circle
                    key={s.key}
                    cx="21"
                    cy="21"
                    r={RADIUS}
                    fill="none"
                    stroke={s.color}
                    strokeWidth="5"
                    strokeDasharray={`${s.share} ${100 - s.share}`}
                    strokeDashoffset={-s.offset}
                  />
                ))}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-base font-semibold leading-6 text-white">{total}</span>
                <span className="text-[10px] leading-4 text-[#a3a3a3]">New Listings</span>
              </div>
            </div>
            <ul className="flex min-w-0 flex-1 flex-col gap-1">
              {segments.map((s) => (
                <li key={s.key} className="flex items-center gap-2 text-sm leading-5">
                  <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
                  <span className="min-w-0 flex-1 truncate text-[#a3a3a3]">{s.label}</span>
                  <span className="tabular-nums text-[#a3a3a3]">{Math.round(s.share)}%</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Recent activity */}
      <div className="glass flex flex-col gap-4 px-3 py-4 backdrop-blur-[6px]">
        <div className="flex items-center justify-between">
          <p className="text-base font-semibold leading-6 text-white">Recent Activity</p>
          {onViewAll && (
            <button
              type="button"
              onClick={onViewAll}
              className="text-sm font-semibold leading-5 text-[#00c27e] hover:underline"
            >
              View All
            </button>
          )}
        </div>
        <ul className="flex flex-col gap-3">
          {recent.map((alert) => (
            <li key={alert.id} className="flex items-center gap-3 text-sm leading-5">
              <span className="flex w-[90px] shrink-0 items-center gap-2">
                <TokenIcon
                  symbol={alert.currency}
                  logo={alert.tokenMetadata?.logo}
                  size="sm"
                />
                <span className="truncate font-medium text-white">{alert.currency}</span>
              </span>
              <span className="min-w-0 flex-1 truncate text-[#a3a3a3]">
                Listed on {exchangeBrand(alert.exchange).name}
              </span>
              <span className="shrink-0 text-[#a3a3a3]">{formatTimeAgo(alert.createdAt)}</span>
            </li>
          ))}
          {leaderboard && recent.length === 0 && (
            <li className="text-sm text-[#a3a3a3]">No listings yet.</li>
          )}
        </ul>
      </div>
    </div>
  );
};
